import {
  planAlertEntitySchema,
  planAlertIdentifierSchema,
  scenarioAlertEntitySchema,
  scenarioAlertIdentifierSchema,
  scenarioChangesSchema,
  scenarioStatusSchema,
} from '@tempo-io/adaptive-planner-dto';
import {
  syncStatusEntitySchema,
  teamEntitySchema,
  teamIdentifierSchema,
} from '@tempo-io/capacity-dto';
import { integrationEntitySchema, integrationIdentifierSchema } from '@tempo-io/item-store-dto';
import z from 'zod';
import {
  exportCompletionVariablesSchema,
  feedbackVariablesSchema,
  ideaCommentsVariablesSchema,
  invitesVariablesSchema,
  jiraReauthVariablesSchema,
  portfolioCreatedVariablesSchema,
  trialsVariablesSchema,
  workIntegrationScheduleDisabledVariablesSchema,
} from './notification-dto';

const workIntegrationSseBaseSchema = z.object({
  entityType: z.literal('work-integration'),
  organizationId: z.string(),
  timestamp: z.string(),
});

export const workIntegrationSseDtoSchema = z.discriminatedUnion('eventType', [
  workIntegrationSseBaseSchema.extend({
    eventType: z.literal('created'),
    entity: integrationEntitySchema,
  }),
  workIntegrationSseBaseSchema.extend({
    eventType: z.literal('updated_full'),
    entity: integrationEntitySchema,
  }),
  workIntegrationSseBaseSchema.extend({
    eventType: z.literal('deleted'),
    entity: integrationIdentifierSchema,
  }),
]);

export const syncStatusSseDtoSchema = z.object({
  entityType: z.literal('sync-status'),
  entity: syncStatusEntitySchema,
  organizationId: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  timestamp: z.string(),
});

const teamSseBaseSchema = z.object({
  entityType: z.literal('team'),
  organizationId: z.string(),
  timestamp: z.string(),
});

export const teamSseDtoSchema = z.discriminatedUnion('eventType', [
  teamSseBaseSchema.extend({
    eventType: z.literal('updated_full'),
    entity: teamEntitySchema,
  }),
  teamSseBaseSchema.extend({
    eventType: z.literal('deleted'),
    entity: teamIdentifierSchema,
  }),
]);

export const membershipSseDtoSchema = z.object({
  entityType: z.literal('membership'),
  entity: z.object({
    id: z.string(),
    userId: z.string(),
    organizationId: z.string(),
    role: z.string().optional(),
  }),
  organizationId: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  timestamp: z.string(),
});

const notificationEntityBaseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  title: z.string(),
  body: z.string(),
});

const invitesEntitySchema = notificationEntityBaseSchema.extend({
  type: z.literal('organization'),
  variables: invitesVariablesSchema,
});

const trialsEntitySchema = notificationEntityBaseSchema.extend({
  type: z.literal('organization'),
  variables: trialsVariablesSchema,
});

const jiraReauthEntitySchema = notificationEntityBaseSchema.extend({
  type: z.literal('organization'),
  variables: jiraReauthVariablesSchema,
});

const workIntegrationScheduleDisabledEntitySchema = notificationEntityBaseSchema.extend({
  type: z.literal('organization'),
  variables: workIntegrationScheduleDisabledVariablesSchema,
});

const portfolioCreatedEntitySchema = notificationEntityBaseSchema.extend({
  type: z.literal('organization'),
  variables: portfolioCreatedVariablesSchema,
});

const feedbackEntitySchema = notificationEntityBaseSchema.extend({
  type: z.literal('subscription'),
  variables: feedbackVariablesSchema,
});

const ideaCommentsEntitySchema = notificationEntityBaseSchema.extend({
  type: z.literal('subscription'),
  variables: ideaCommentsVariablesSchema,
});

const exportCompletionEntitySchema = notificationEntityBaseSchema.extend({
  type: z.literal('subscription'),
  variables: exportCompletionVariablesSchema,
});

export const notificationSseEntitySchema = z.union([
  invitesEntitySchema,
  trialsEntitySchema,
  jiraReauthEntitySchema,
  workIntegrationScheduleDisabledEntitySchema,
  portfolioCreatedEntitySchema,
  feedbackEntitySchema,
  ideaCommentsEntitySchema,
  exportCompletionEntitySchema,
]);

export type NotificationSseEntity = z.infer<typeof notificationSseEntitySchema>;

export const notificationSseDtoSchema = z.object({
  entityType: z.literal('notification'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.literal('created'),
  entity: notificationSseEntitySchema,
});

const scenarioSseBaseSchema = z.object({
  entityType: z.literal('scenario'),
  organizationId: z.string(),
  timestamp: z.string(),
  loopId: z.number().int(),
});

export const scenarioSseDtoSchema = z.discriminatedUnion('eventType', [
  scenarioSseBaseSchema.extend({
    eventType: z.literal('created'),
    entity: z.object({
      id: z.number().int(),
      loopId: z.number().int(),
      status: scenarioStatusSchema,
      name: z.string(),
    }),
  }),
  scenarioSseBaseSchema.extend({
    eventType: z.literal('updated_full'),
    entity: z.object({
      id: z.number().int(),
      loopId: z.number().int(),
      status: scenarioStatusSchema,
      name: z.string(),
    }),
  }),
  scenarioSseBaseSchema.extend({
    eventType: z.literal('updated_partial'),
    change: scenarioChangesSchema,
  }),
]);

export const slackWorkspaceSseDtoSchema = z.object({
  entityType: z.literal('slack-workspace'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  entity: z.object({
    slackWorkspaceId: z.string(),
    connected: z.boolean(),
    slackWorkspaceName: z.string().nullish(),
  }),
});

const planAlertSseBaseSchema = z.object({
  entityType: z.literal('plan-alert'),
  organizationId: z.string(),
  timestamp: z.string(),
  planId: z.number().int(),
});

export const planAlertSseDtoSchema = z.discriminatedUnion('eventType', [
  planAlertSseBaseSchema.extend({
    eventType: z.literal('created'),
    entity: planAlertEntitySchema,
  }),
  planAlertSseBaseSchema.extend({
    eventType: z.literal('deleted'),
    entity: planAlertIdentifierSchema,
  }),
]);

export const planSseDtoSchema = z.object({
  entityType: z.literal('plan'),
  organizationId: z.string(),
  timestamp: z.string(),
  planId: z.number().int(),
  eventType: z.literal('updated_partial'),
  entity: z.object({
    id: z.number().int(),
  }),
});

export const scenarioAlertSseDtoSchema = z.discriminatedUnion('eventType', [
  z.object({
    entityType: z.literal('scenario-alert'),
    organizationId: z.string(),
    timestamp: z.string(),
    scenarioId: z.number().int(),
    eventType: z.literal('created'),
    entity: scenarioAlertEntitySchema,
  }),
  z.object({
    entityType: z.literal('scenario-alert'),
    organizationId: z.string(),
    timestamp: z.string(),
    eventType: z.literal('deleted'),
    entity: scenarioAlertIdentifierSchema,
  }),
]);

export const itemTypeSseDtoSchema = z.object({
  entityType: z.literal('item-type'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  entity: z.object({ id: z.number().int() }).loose(),
});

export const itemTypeLevelSseDtoSchema = z.object({
  entityType: z.literal('item-type-level'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  entity: z.object({ id: z.number().int() }).loose(),
});

const loopScheduleSseBaseSchema = z.object({
  entityType: z.literal('loop-schedule'),
  organizationId: z.string(),
  timestamp: z.string(),
});

export const loopScheduleSseDtoSchema = z.discriminatedUnion('eventType', [
  loopScheduleSseBaseSchema.extend({
    eventType: z.literal('updated_full'),
    entity: z.object({
      id: z.number().int(),
      itemId: z.number().int().optional(),
    }),
  }),
]);

export type LoopScheduleSseDto = z.output<typeof loopScheduleSseDtoSchema>;

export const sseDtoSchema = z.discriminatedUnion('entityType', [
  workIntegrationSseDtoSchema,
  syncStatusSseDtoSchema,
  teamSseDtoSchema,
  membershipSseDtoSchema,
  notificationSseDtoSchema,
  scenarioSseDtoSchema,
  slackWorkspaceSseDtoSchema,
  planAlertSseDtoSchema,
  planSseDtoSchema,
  scenarioAlertSseDtoSchema,
  itemTypeSseDtoSchema,
  itemTypeLevelSseDtoSchema,
  loopScheduleSseDtoSchema,
]);

export type TeamSseDto = z.infer<typeof teamSseDtoSchema>;
export type ScenarioSseDto = z.infer<typeof scenarioSseDtoSchema>;
export type ScenarioAlertSseDto = z.infer<typeof scenarioAlertSseDtoSchema>;
